import { Material } from "three";
import { frameSettings as FrameSettings } from "shared/utils/doorConfigurations";
import { getFramePositions, getFrameScaling } from "shared/utils/door";
import { BeamVertical } from "../../components/BeamVertical";
import { getGeometryBounds2D } from "../../components/openingGeometry";
import { TaatsGLTFResult } from "../TaatsType";
import type { VerticalBeamPosition } from "entities/configurator/model/layoutMeta";
import { standingDoorBeamInsetMeters } from "entities/configurator/model/layoutGeometry";
import { getLocalVerticalBeamX } from "../../layouts/verticalLayoutUtils";

export const TaatsVerticalLayout = ({
  nodes,
  material,
  width,
  height,
  localPosition,
  frameSettings,
}: {
  nodes: TaatsGLTFResult["nodes"];
  material: Material;
  width: number;
  height: number;
  localPosition: VerticalBeamPosition | undefined;
  frameSettings?: FrameSettings;
}) => {
  const isVisible = localPosition !== undefined;
  const framePositions = getFramePositions(width, height);
  const frameScaling = getFrameScaling(width, height);
  const leftBounds = getGeometryBounds2D(nodes.frame_left.geometry);
  const rightBounds = getGeometryBounds2D(nodes.frame_right.geometry);
  const topBounds = getGeometryBounds2D(nodes.frame_top.geometry);
  const bottomBounds = getGeometryBounds2D(nodes.frame_bottom.geometry);
  const beamBounds = getGeometryBounds2D(nodes.beam_vertical.geometry);
  const parentScaleX = frameSettings?.generalScaleX ?? 1;
  const frameScaleY = frameSettings?.generalScaleY ?? 1;

  const left =
    leftBounds.minX *
      (frameScaling.vertical.x + (frameSettings?.frameLeft?.scaleX ?? 0)) +
    framePositions.left.x +
    (frameSettings?.frameLeft?.shiftX ?? 0);
  const right =
    rightBounds.maxX *
      (frameScaling.vertical.x + (frameSettings?.frameRight?.scaleX ?? 0)) +
    framePositions.right.x +
    (frameSettings?.frameRight?.shiftX ?? 0);
  // Match the frame-only group's transform in Taats, including its rail adjustments.
  const top =
    (topBounds.minY *
      (frameSettings?.frameTop?.scaleY ?? frameScaling.horizontal.y) +
      framePositions.top.y +
      (frameSettings?.frameTop?.shiftY ?? 0)) *
      frameScaleY -
    0.007;
  const bottom =
    (bottomBounds.maxY +
      framePositions.bottom.y +
      (frameSettings?.frameBottom?.shiftY ?? 0)) *
      frameScaleY -
    0.007;
  const beamX = isVisible
    ? getLocalVerticalBeamX(localPosition, left, right, beamBounds, {
        insetMeters: standingDoorBeamInsetMeters,
        parentScaleX,
      })
    : 0;

  return (
    <BeamVertical
      visible={isVisible}
      geometry={nodes.beam_vertical.geometry}
      material={material}
      x={beamX}
      bottom={bottom}
      top={top}
      scaleX={1 / parentScaleX}
    />
  );
};
